import { useState } from 'react'
import { Alert, Button, Modal, Spin, Typography } from 'antd'
import { faTimes } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import MerchantApplicationProvider, { useMerchantApplication } from './MerchantApplicationContext'
import { MerchantApplicationSteps } from './merchantApplicationModel'
import StepProgress from './components/StepProgress'
import ConfirmInfoStep from './steps/ConfirmInfoStep'
import BusinessDetailsStep from './steps/BusinessDetailsStep'
import BusinessOwnersStep from './steps/BusinessOwnersStep'
import BankAccountStep from './steps/BankAccountStep'
import DocumentsStep from './steps/DocumentsStep'
import ApplicationSubmitted from './steps/ApplicationSubmitted'
import SidePanel from './components/SidePanel'
import './MerchantApplicationModal.css'

const { Title, Text } = Typography

type Props = {
  open: boolean
  onClose: () => void
  onApplicationSubmitted: () => void
}

type ContentProps = {
  onClose: () => void
  onApplicationSubmitted: () => void
}

const MerchantApplicationContent = ({ onClose, onApplicationSubmitted }: ContentProps) => {
  const { state, dispatch, preFillError } = useMerchantApplication()
  const [showExitConfirm, setShowExitConfirm] = useState(false)

  const handleExit = () => setShowExitConfirm(true)
  const handleConfirmExit = () => {
    setShowExitConfirm(false)
    onClose()
  }
  const handleBack = () => dispatch({ type: 'PREV_STEP' })
  const handleNext = () => dispatch({ type: 'NEXT_STEP' })

  const exitConfirmModal = (
    <Modal
      open={showExitConfirm}
      onCancel={() => setShowExitConfirm(false)}
      footer={null}
      centered
      width={400}
    >
      <div style={{ textAlign: 'center', padding: '16px 0' }}>
        <Title level={4} style={{ marginBottom: 8 }}>Exit Application?</Title>
        <Text type="secondary">
          Your progress won't be saved. You can restart the application at any time.
        </Text>
        <div style={{ marginTop: 24, display: 'flex', gap: 12, justifyContent: 'center' }}>
          <Button size="large" onClick={() => setShowExitConfirm(false)}>
            Continue Application
          </Button>
          <Button size="large" type="primary" danger onClick={handleConfirmExit}>
            Exit
          </Button>
        </div>
      </div>
    </Modal>
  )

  if (state.isSubmitted) {
    return (
      <>
        {exitConfirmModal}
        <div className="merchant-app-layout">
          <div className="merchant-app-left">
            <ApplicationSubmitted onClose={onClose} />
          </div>
          <SidePanel />
        </div>
      </>
    )
  }

  if (state.isLoading) {
    return (
      <div className="merchant-app-layout">
        <button className="merchant-app-close-button" onClick={handleExit} aria-label="Exit">
          <FontAwesomeIcon icon={faTimes} />
        </button>
        <div className="merchant-app-left" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Spin size="large" />
        </div>
        <SidePanel />
      </div>
    )
  }

  if (preFillError) {
    return (
      <div className="merchant-app-layout">
        <button className="merchant-app-close-button" onClick={onClose} aria-label="Close">
          <FontAwesomeIcon icon={faTimes} />
        </button>
        <div className="merchant-app-left" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 48 }}>
          <Alert
            type="error"
            message="Unable to load application"
            description="Something went wrong loading your information. Please close and try again."
            showIcon
          />
        </div>
        <SidePanel />
      </div>
    )
  }

  const renderStep = () => {
    switch (state.currentStep) {
      case MerchantApplicationSteps.ConfirmInfo:
        return <ConfirmInfoStep onNext={handleNext} />
      case MerchantApplicationSteps.BusinessDetails:
        return <BusinessDetailsStep onBack={handleBack} onNext={handleNext} />
      case MerchantApplicationSteps.BusinessOwners:
        return <BusinessOwnersStep onBack={handleBack} onNext={handleNext} />
      case MerchantApplicationSteps.BankAccount:
        return <BankAccountStep onBack={handleBack} onNext={handleNext} />
      case MerchantApplicationSteps.Documents:
        return <DocumentsStep onBack={handleBack} onSubmitted={onApplicationSubmitted} />
      default:
        return null
    }
  }

  return (
    <>
      {exitConfirmModal}
      <div className="merchant-app-layout">
        <button className="merchant-app-close-button" onClick={handleExit} aria-label="Exit">
          <FontAwesomeIcon icon={faTimes} />
        </button>
        <div className="merchant-app-left">
          <StepProgress currentStep={state.currentStep} />
          {renderStep()}
        </div>
        <SidePanel />
      </div>
    </>
  )
}

const MerchantApplicationModal = ({ open, onClose, onApplicationSubmitted }: Props) => {
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width="100vw"
      closable={false}
      keyboard={false}
      className="merchant-app-modal"
      styles={{ body: { padding: 0 } }}
      style={{ top: 0, maxWidth: '100vw', margin: 0, paddingBottom: 0 }}
      destroyOnHidden
    >
      <MerchantApplicationProvider>
        <MerchantApplicationContent onClose={onClose} onApplicationSubmitted={onApplicationSubmitted} />
      </MerchantApplicationProvider>
    </Modal>
  )
}

export default MerchantApplicationModal
